import React, { useEffect } from 'react';

const PaymentStatusModal = ({ show, onClose, status }) => {
    const isSuccess = status?.success;

    useEffect(() => {
        if (show) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [show]);

    if (!show) return null;

    const defaultErrorMessage = {
        firstPart: 'Sorry, we were unable to process your payment. Please ensure all details were entered correctly and try again.',
        secondPart: 'If the problem continues, try a different payment method or contact your bank.'
    };

    const errorMessageToDisplay = status?.errorMessage || defaultErrorMessage;

    return (
        <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="modal-content bg-white p-6 sm:p-8 rounded-xl shadow-2xl w-full max-w-sm transform transition-all"
                onClick={e => e.stopPropagation()}
            >
                <div className="absolute top-0 right-0 pt-4 pr-4">
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 focus:outline-none">
                        <span className="sr-only">Close</span>
                        <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {isSuccess ? (
                    // Success
                    <div className="text-center">
                        <div className="success-icon mb-3 text-green-500">
                            <i className="fas fa-check-circle fa-4x"></i>
                        </div>
                        <h4 className="text-xl font-semibold mb-2">Payment Successful</h4>
                        <p className="text-sm text-gray-600 mb-4">Your payment has been processed successfully.</p>
                        {status?.message?.invoices && (
                            <div className="mt-3 text-xs text-gray-500">
                                <h5>Payment Details:</h5>
                                {status.message.invoices.map((invoice, index) => (
                                    <div key={index} className="mb-1">
                                        <p className="mb-0">Order #: {invoice.orderNumber} - Amount: ${invoice.paymentAmount}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="mt-6 flex justify-center">
                            <button
                                className={`font-semibold py-2 px-8 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-green-600 hover:bg-green-700 text-white focus:ring-green-500`}
                                onClick={onClose}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                ) : (
                    // Failure
                    <div>
                        <div className="flex items-center space-x-2 mb-4">
                            <svg
                                version="1.1"
                                id="Capa_1"
                                xmlns="http://www.w3.org/2000/svg"
                                xmlnsXlink="http://www.w3.org/1999/xlink"
                                x="0px"
                                y="0px"
                                width="35px"
                                height="35px"
                                viewBox="0 0 45.311 45.311"
                                style={{ enableBackground: "new 0 0 45.311 45.311" }}
                                xmlSpace="preserve"
                            >
                                <g>
                                    <path
                                        fill="red"
                                        d="M22.675,0.02c-0.006,0-0.014,0.001-0.02,0.001c-0.007,0-0.013-0.001-0.02-0.001C10.135,0.02,0,10.154,0,22.656c0,12.5,10.135,22.635,22.635,22.635c0.007,0,0.013,0,0.02,0c0.006,0,0.014,0,0.02,0c12.5,0,22.635-10.135,22.635-22.635C45.311,10.154,35.176,0.02,22.675,0.02z M22.675,38.811c-0.006,0-0.014-0.001-0.02-0.001c-0.007,0-0.013,0.001-0.02,0.001c-2.046,0-3.705-1.658-3.705-3.705c0-2.045,1.659-3.703,3.705-3.703c0.007,0,0.013,0,0.02,0c0.006,0,0.014,0,0.02,0c2.045,0,3.706,1.658,3.706,3.703C26.381,37.152,24.723,38.811,22.675,38.811z M27.988,10.578c-0.242,3.697-1.932,14.692-1.932,14.692c0,1.854-1.519,3.356-3.373,3.356c-0.01,0-0.02,0-0.029,0c-0.009,0-0.02,0-0.029,0c-1.853,0-3.372-1.504-3.372-3.356c0,0-1.689-10.995-1.931-14.692C17.202,8.727,18.62,5.29,22.626,5.29c0.01,0,0.02,0.001,0.029,0.001c0.009,0,0.019-0.001,0.029-0.001C26.689,5.29,28.109,8.727,27.988,10.578z"
                                    />
                                </g>
                            </svg>
                            <h4 className="text-xl font-semibold text-gray-800">Payment Failed</h4>
                        </div>

                        {/* Error Message */}
                        {typeof errorMessageToDisplay === 'object' ? (
                            <div className="text-sm text-gray-600 mb-6 leading-relaxed">
                                <p className="mb-6">{errorMessageToDisplay.firstPart}</p><br/><br/>
                                <p className="mt-6">{errorMessageToDisplay.secondPart}</p>
                            </div>
                        ) : (
                            <p
                                className="text-base font-bold text-gray-600 mb-6 leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: errorMessageToDisplay }}
                            />
                        )}

                        {/* OK Button */}
                        <div className="flex justify-end">
                            <button
                                className={`font-semibold py-2 px-10 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50 bg-blue-800 hover:bg-blue-900 text-white focus:ring-blue-600`}
                                onClick={onClose}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentStatusModal;